import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, X, Plus, Palette, Globe, Building2, Sparkles, Save, Trash2 } from 'lucide-react';

interface WhiteLabelConfig {
  id: string;
  organization_id: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  custom_css: string | null;
  allowed_domains: string[] | null;
  is_active: boolean;
}

interface WhiteLabelSettingsProps {
  organizationId: string;
  isAdmin: boolean;
}

export function WhiteLabelSettings({ organizationId, isAdmin }: WhiteLabelSettingsProps) {
  const [config, setConfig] = useState<WhiteLabelConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#d4a017');
  const [secondaryColor, setSecondaryColor] = useState('#2563eb');
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchConfig();
  }, [organizationId]);

  async function fetchConfig() {
    try {
      const { data, error } = await supabase
        .from('white_label_configs')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setConfig(data);
        setCompanyName(data.company_name);
        setPrimaryColor(data.primary_color || '#d4a017');
        setSecondaryColor(data.secondary_color || '#2563eb');
        setAllowedDomains(data.allowed_domains || []);
        setIsActive(data.is_active);
        setLogoUrl(data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching white label config:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Logo must be under 2MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${organizationId}/logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('org-logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('org-logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);

      toast({
        title: 'Logo uploaded',
        description: 'Your logo has been uploaded successfully',
      });
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  }

  async function removeLogo() {
    try {
      const { error } = await supabase.storage
        .from('org-logos')
        .remove([`${organizationId}/logo.png`, `${organizationId}/logo.jpg`, `${organizationId}/logo.webp`]);

      if (error) throw error;

      setLogoUrl(null);
      toast({
        title: 'Logo removed',
        description: 'Your logo has been removed',
      });
    } catch (error: any) {
      console.error('Error removing logo:', error);
    }
  }

  function addDomain() {
    if (!newDomain.trim()) return;
    
    // Basic domain validation
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      toast({
        title: 'Invalid domain',
        description: 'Please enter a valid domain (e.g., example.com)',
        variant: 'destructive',
      });
      return;
    }

    if (allowedDomains.includes(newDomain.trim())) {
      toast({
        title: 'Domain exists',
        description: 'This domain is already in the list',
        variant: 'destructive',
      });
      return;
    }

    setAllowedDomains([...allowedDomains, newDomain.trim()]);
    setNewDomain('');
  }

  function removeDomain(domain: string) {
    setAllowedDomains(allowedDomains.filter(d => d !== domain));
  }

  async function handleSave() {
    if (!companyName.trim()) {
      toast({
        title: 'Company name required',
        description: 'Please enter a company name',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    try {
      const configData = {
        organization_id: organizationId,
        company_name: companyName.trim(),
        logo_url: logoUrl,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        allowed_domains: allowedDomains,
        is_active: isActive,
      };

      if (config) {
        // Update existing config
        const { error } = await supabase
          .from('white_label_configs')
          .update(configData)
          .eq('id', config.id);

        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from('white_label_configs')
          .insert(configData);

        if (error) throw error;
      }

      toast({
        title: 'Settings saved',
        description: 'Your white label settings have been saved',
      });

      fetchConfig();
    } catch (error: any) {
      toast({
        title: 'Save failed',
        description: error.message || 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Only organization admins can manage white label settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">White Label Settings</h2>
            <p className="text-sm text-muted-foreground">Customize the branding for your embedded analyzer</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="active-toggle" className="text-sm text-muted-foreground">Active</Label>
          <Switch
            id="active-toggle"
            checked={isActive}
            onCheckedChange={setIsActive}
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Branding Section */}
        <div className="space-y-6">
          <div className="glass-panel rounded-xl p-6 border border-border/50">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Company Branding</h3>
            </div>

            {/* Company Name */}
            <div className="space-y-2 mb-6">
              <Label htmlFor="company-name">Company Name</Label>
              <Input
                id="company-name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your Company Name"
                className="bg-card/50 border-border/50"
              />
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-start gap-4">
                {logoUrl ? (
                  <div className="relative">
                    <div className="w-24 h-24 rounded-xl bg-card/50 border border-border/50 flex items-center justify-center overflow-hidden">
                      <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                    </div>
                    <button
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:opacity-80 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <label className="w-24 h-24 rounded-xl bg-card/50 border-2 border-dashed border-border/50 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground mt-1">Upload</span>
                      </>
                    )}
                  </label>
                )}
                <div className="text-sm text-muted-foreground">
                  <p>Recommended: 200x200px</p>
                  <p>Max size: 2MB</p>
                  <p>PNG, JPG, or WebP</p>
                </div>
              </div>
            </div>
          </div>

          {/* Colors Section */}
          <div className="glass-panel rounded-xl p-6 border border-border/50">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Brand Colors</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary-color">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="primary-color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-border/50 cursor-pointer"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 bg-card/50 border-border/50 font-mono text-sm"
                    placeholder="#d4a017"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondary-color">Secondary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id="secondary-color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-border/50 cursor-pointer"
                  />
                  <Input
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="flex-1 bg-card/50 border-border/50 font-mono text-sm"
                    placeholder="#2563eb"
                  />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="mt-4 p-4 rounded-xl bg-card/50 border border-border/50">
              <p className="text-sm text-muted-foreground mb-2">Preview</p>
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-10 rounded-lg" 
                  style={{ backgroundColor: primaryColor }}
                />
                <div 
                  className="w-16 h-10 rounded-lg" 
                  style={{ backgroundColor: secondaryColor }}
                />
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  Button
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Domains Section */}
        <div className="space-y-6">
          <div className="glass-panel rounded-xl p-6 border border-border/50">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Allowed Domains</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Specify which domains can embed your white-labeled analyzer. Leave empty to allow all domains.
            </p>

            {/* Add Domain */}
            <div className="flex items-center gap-2 mb-4">
              <Input
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="flex-1 bg-card/50 border-border/50"
                onKeyDown={(e) => e.key === 'Enter' && addDomain()}
              />
              <Button onClick={addDomain} size="icon" variant="outline">
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Domain List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {allowedDomains.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <Globe className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No domains configured</p>
                  <p className="text-xs">All domains can embed your analyzer</p>
                </div>
              ) : (
                allowedDomains.map((domain) => (
                  <div
                    key={domain}
                    className="flex items-center justify-between px-4 py-2 rounded-lg bg-card/50 border border-border/50"
                  >
                    <span className="text-sm font-mono">{domain}</span>
                    <button
                      onClick={() => removeDomain(domain)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Embed Code Preview */}
          {isActive && (
            <div className="glass-panel rounded-xl p-6 border border-accent/30 bg-accent/5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-accent" />
                <h3 className="font-semibold text-foreground">Embed Code</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Add this script to your website to embed the analyzer:
              </p>
              <div className="bg-card/80 rounded-lg p-4 overflow-x-auto">
                <code className="text-xs text-foreground font-mono whitespace-pre">
{`<script src="${window.location.origin}/embed.js"></script>
<div 
  id="credit-analyzer" 
  data-org="${organizationId}"
></div>`}
                </code>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-glow bg-gradient-to-r from-primary to-primary/90"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
